import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Select,
  DatePicker,
  InputNumber,
  Tooltip,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";

import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";

import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";

import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";

import { BOM } from "../../../services/definedMethod";

// ─────────────────────────────────────────────────────────────
// Static Options
// ─────────────────────────────────────────────────────────────

const BOND_TYPE_OPTIONS = [
  { label: "Fixed Rate Bond", value: "FIXED_RATE_BOND" },
  { label: "Floating Rate Bond", value: "FLOATING_RATE_BOND" },
  { label: "Zero Coupon Bond", value: "ZERO_COUPON_BOND" },
];

const PAYMENT_TYPE_OPTIONS = [
  { label: "At Maturity", value: 1 },
  { label: "Periodic", value: 2 },
  { label: "Upfront", value: 3 },
];

const SCORE_OPTIONS = [
  { label: "Not Done / Pending", value: 0 },
  { label: "Done / Cleared", value: 100 },
];

const SETTLEMENT_ACCOUNT_OPTIONS = [
  {
    label: "*****4901 — John Doe",
    value: 4101,
  },
];
// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

const Tip = ({ text, tip }) => (
  <span>
    {text}
    <Tooltip title={tip}>
      <InfoCircleOutlined
        style={{
          marginLeft: 4,
          color: "#667085",
        }}
      />
    </Tooltip>
  </span>
);

const SectionHeader = ({ title }) => (
  <div style={{ marginBottom: 16, marginTop: 8 }}>
    <p
      style={{
        margin: 0,
        fontWeight: 600,
        fontSize: 14,
        color: "#344054",
      }}
    >
      {title}
    </p>

    <div
      style={{
        borderBottom: "1px solid #EAECF0",
        marginTop: 6,
      }}
    />
  </div>
);

const parseDate = (value) => {
  if (!value) return null;

  if (Array.isArray(value)) {
    const [y, m, d] = value;

    return dayjs(
      `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    );
  }

  return dayjs(value);
};

const parseNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return value;
  }

  const parsed = Number(String(value).replace(/,/g, ""));

  return Number.isNaN(parsed) ? null : parsed;
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

const AddBondContract = (props) => {
  const [form] = Form.useForm();

  const navigate = useNavigate();
  const location = useLocation();

  const { userData } = props;
  const { session } = userData || {};

  const contractId = location.state || null;
  const isEdit = !!contractId;

  // ─────────────────────────────────────────────────────────
  // Loading States
  // ─────────────────────────────────────────────────────────

  const [isLoading, setIsLoading] = useState(false);
  const [clientSearching, setClientSearching] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [clientSearchValue, setClientSearchValue] = useState("");
  // ─────────────────────────────────────────────────────────
  // LOV States
  // ─────────────────────────────────────────────────────────

  const [clientOptions, setClientOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [settlementAccountOptions, setSettlementAccountOptions] = useState(
    SETTLEMENT_ACCOUNT_OPTIONS,
  );
  const [custodianOptions, setCustodianOptions] = useState([]);
  // const [linkedAccountOptions, setLinkedAccountOptions] = useState(SETTLEMENT_ACCOUNT_OPTIONS);

  const [investorTypeOptions, setInvestorTypeOptions] = useState(SCORE_OPTIONS);
  const [bondTypeOptions, setBondTypeOptions] = useState(BOND_TYPE_OPTIONS);
  const [paymentTypeOptions, setPaymentTypeOptions] = useState([]);

  const [amlScreeningOptions, setAmlScreeningOptions] = useState([]);
  const [centralBankReportOptions, setCentralBankReportOptions] = useState([]);
  const [sanctionScreeningOptions, setSanctionScreeningOptions] = useState([]);
  const [kycStatusOptions, setKycStatusOptions] = useState([]);
  const [issuerRiskRatingOptions, setIssuerRiskRatingOptions] = useState([]);
  const [marketRiskConsiderationOptions, setMarketRiskConsiderationOptions] =
    useState([]);
  const [documentStatusOptions, setDocumentStatusOptions] = useState([]);
  const [securityMasterOptions, setSecurityMasterOptions] = useState([]);
  // ─────────────────────────────────────────────────────────
  // Tracking
  // ─────────────────────────────────────────────────────────

  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState(null);

  // ─────────────────────────────────────────────────────────
  // Effects
  // ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (isEdit && contractId) {
      fetchContractDetail(contractId);
    }
  }, [contractId]);

  // ─────────────────────────────────────────────────────────
  // Client Search — same pattern as Treasury Bill
  // ─────────────────────────────────────────────────────────

  // REPLACE the existing searchClients with this:
const searchClients = useCallback(
  (searchValue) => {
    setClientSearchValue(searchValue);
    if (!searchValue || searchValue.length < 2) {
      setClientOptions([]);
      return;
    }
    setClientSearching(true);
    const body = { session };
    const url = `${apiConfig.GetSearch}?exactMatch=true&query=${encodeURIComponent(
      searchValue.trim(),
    )}&resource=clients,clientIdentifiers`;

    BOM.FetchReqAction(body, url, (err, res) => {
      setClientSearching(false);
      if (err) return;
      const items = Array.isArray(res) ? res : [];
      setClientOptions(
        items.map((c) => ({
          label: `${c.entityAccountNo ?? ""} — ${c.entityName ?? ""}`,
          value: c.entityId,
          displayName: c.entityName,
          accountNo: c.entityAccountNo,
          externalId: c.entityExternalId,
        })),
      );
    });
  },
  [session],
);

// REPLACE handleClientSelect with this:
const handleClientSelect = (clientId) => {
  setSelectedClientId(clientId);
  setSelectedClientName(
    clientOptions.find((c) => c.value === clientId)?.displayName ?? "",
  );

  form.setFieldsValue({
    productId: null,
    settlementAccountId: null,
    custodian: null,
  });

  setProductOptions([]);
  setCustodianOptions([]);
  fetchTemplate(clientId);
};

  // ─────────────────────────────────────────────────────────
  // Template Fetch
  // ─────────────────────────────────────────────────────────

  const fetchTemplate = (clientId) => {
    setTemplateLoading(true);

    const body = { session };
    const url = `${apiConfig.GetBondContracts}/template?clientId=${clientId}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      setTemplateLoading(false);

      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load template.",
        );
        return;
      }

      // Products
      if (Array.isArray(res?.productOptions)) {
        setProductOptions(
          res.productOptions.map((p) => ({
            label: p.productName ?? p.name ?? `Product ${p.id}`,
            value: p.id,
          })),
        );
      }

      // Settlement Accounts
      // const accounts =
      //   res?.settlementAccountOptions ?? res?.linkedAccountOptions;

      // if (Array.isArray(accounts)) {
      //   setSettlementAccountOptions(
      //     accounts.map((a) => ({
      //       label: a.accountNo
      //         ? `${a.accountNo}${a.clientName ? ` — ${a.clientName}` : ""}`
      //         : `Account ${a.id}`,
      //       value: a.id,
      //     })),
      //   );
      // }

      // Custodians
      if (Array.isArray(res?.custodians)) {
        setCustodianOptions(
          res.custodians.map((c) => ({
            label: c.name ?? `Custodian ${c.id}`,
            value: c.id,
          })),
        );
      }

      const mapOpts = (arr) =>
        arr.map((o) => ({ label: o.text, value: o.value }));

      if (Array.isArray(res?.amlScreeningOptions))
        setAmlScreeningOptions(mapOpts(res.amlScreeningOptions));
      if (Array.isArray(res?.centralBankReportOptions))
        setCentralBankReportOptions(mapOpts(res.centralBankReportOptions));
      if (Array.isArray(res?.sanctionScreeningOptions))
        setSanctionScreeningOptions(mapOpts(res.sanctionScreeningOptions));
      if (Array.isArray(res?.kycStatusOptions))
        setKycStatusOptions(mapOpts(res.kycStatusOptions));
      if (Array.isArray(res?.issuerRiskRatingOptions))
        setIssuerRiskRatingOptions(mapOpts(res.issuerRiskRatingOptions));
      if (Array.isArray(res?.marketRiskConsiderationOptions))
        setMarketRiskConsiderationOptions(
          mapOpts(res.marketRiskConsiderationOptions),
        );
      if (Array.isArray(res?.documentStatusOptions))
        setDocumentStatusOptions(mapOpts(res.documentStatusOptions));
      if (Array.isArray(res?.investorTypeOptions))
        setInvestorTypeOptions(mapOpts(res.investorTypeOptions));

      if (Array.isArray(res?.securityMasterOptions))
        setSecurityMasterOptions(
          res.securityMasterOptions.map((s) => ({
            label: s.instrumentName,
            value: s.id,
          })),
        );

      if (Array.isArray(res?.paymentTypeOptions))
        setPaymentTypeOptions(
          res.paymentTypeOptions.map((s) => ({
            label: s.name,
            value: s.id,
          })),
        );
    });
  };

  // ─────────────────────────────────────────────────────────
  // Fetch existing record for edit
  // ─────────────────────────────────────────────────────────

  const fetchContractDetail = (id) => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetBondContracts}/${id}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      setIsLoading(false);

      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load contract.",
        );
        return;
      }

      console.log("FULL CONTRACT RESPONSE =>", res);

      const clientId = res.client?.id ?? res.clientId;
      const clientName = res.client?.displayName ?? "Client";

      // Seed the client dropdown with this one client so it shows properly
      if (res.client) {
        setClientOptions([
          {
            label: `${clientName}${res.client.accountNo ? ` — ${res.client.accountNo}` : ""}`,
            value: clientId,
            displayName: clientName,
          },
        ]);
      }

      setSelectedClientId(clientId);
      setSelectedClientName(clientName);

      if (clientId) {
        fetchTemplate(clientId);
      }

      const payload = {
        clientId,
        productId: res.product?.id ?? res.productId ?? null,
        settlementAccountId:
          res.settlementAccount?.id ?? res.settlementAccountId ?? null,
        custodian:
          res.custodian?.id ?? res.custodianId ?? res.custodian ?? null,
        dealInitiationDate: parseDate(res.dealInitiationDate),
        issueDate: parseDate(res.issueDate),
        tenor: parseNumber(res.tenor),
        bondType: res.bondType ?? null,
        issuer: res.issuer ?? "",
        investerType: res.investorType?.value ?? res.investerType ?? 100,
        amount: parseNumber(res.amount),
        purchasePrice: parseNumber(res.purchasePrice),
        numberOfUnits: parseNumber(res.numberOfUnits),
        totalConsideration: parseNumber(res.totalConsideration),
        fixedCouponRate: parseNumber(res.fixedCouponRate),
        paymentType: res.paymentType?.value ?? res.paymentType ?? 2,

        // Compliance
        kycStatus: res.kycStatus?.value ?? 0,
        amlScreening: res.amlScreening?.value ?? 100,
        sanctionScreening: res.sanctionScreening?.value ?? 0,
        centralBankReport: res.centralBankReport?.value ?? 0,
        issuerRiskRating: res.issuerRiskRating?.value ?? 0,
        marketRiskConsideration: res.marketRiskConsideration?.value ?? 0,
        concentrationRisk: res.concentrationRisk?.value ?? 0,

        // Documents
        applicationFormStatus: res.applicationFormStatus?.value ?? 100,
        bondPurchaseAgreement: res.bondPurchaseAgreement?.value ?? 100,
        identificationDocuments: res.identificationDocuments?.value ?? 100,
        corporateResolution: res.corporateResolution?.value ?? 100,
        taxClearanceCertificate: res.taxClearanceCertificate?.value ?? 100,
      };

      console.log("FORM PAYLOAD =>", payload);
      form.setFieldsValue(payload);
    });
  };

  // ─────────────────────────────────────────────────────────
  // Submit
  // ─────────────────────────────────────────────────────────

  const onFinish = (values) => {
    const body = {
      session,
      locale: "en",
      dateFormat: "dd MMM yyyy",
      method: isEdit ? "PUT" : "POST",

      productId: values.productId,
      clientId: values.clientId,
      investerType: values.investerType,

      dealInitiationDate: values.dealInitiationDate?.format("DD MMM YYYY"),
      issueDate: values.issueDate?.format("DD MMM YYYY"),

      tenor: values.tenor,
      bondType: values.bondType,
      issuer: values.issuer,
      amount: values.amount,
      purchasePrice: values.purchasePrice,
      numberOfUnits: values.numberOfUnits,
      totalConsideration: values.totalConsideration,
      settlementAccountId: values.settlementAccountId,
      fixedCouponRate: values.fixedCouponRate,
      paymentType: values.paymentType,
      custodian: values.custodian,

      kycStatus: values.kycStatus ?? 0,
      amlScreening: values.amlScreening ?? 100,
      sanctionScreening: values.sanctionScreening ?? 0,
      centralBankReport: values.centralBankReport ?? 0,
      issuerRiskRating: values.issuerRiskRating ?? 0,
      marketRiskConsideration: values.marketRiskConsideration ?? 0,
      concentrationRisk: values.concentrationRisk ?? 0,

      applicationFormStatus: values.applicationFormStatus ?? 100,
      bondPurchaseAgreement: values.bondPurchaseAgreement ?? 100,
      identificationDocuments: values.identificationDocuments ?? 100,
      corporateResolution: values.corporateResolution ?? 100,
      taxClearanceCertificate: values.taxClearanceCertificate ?? 100,
    };

    const url = isEdit
      ? `${apiConfig.GetBondContracts}/${contractId}`
      : `${apiConfig.GetBondContracts}`;

    setIsLoading(true);

    BOM.FetchReqAction(body, url, (err) => {
      setIsLoading(false);

      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            (isEdit
              ? "Failed to update contract."
              : "Failed to create contract."),
        );
        return;
      }

      BOM.AlertMsg(
        cnt.SUCCESS,
        isEdit
          ? "Contract updated successfully."
          : "Bond contract created successfully.",
      );

      navigate(-1);
    });
  };

  // ─────────────────────────────────────────────────────────
  // Shared styles
  // ─────────────────────────────────────────────────────────

  const inputStyle = { width: "100%", height: 44 };
  const fmtAmount = (v) =>
    v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
  const parseAmount = (v) => v?.replace(/,/g, "");
  const dependentFieldsLocked = !selectedClientId && !isEdit;

  return (
    <MainLayout pageItems={staticConfig.manageBondContract}>
      <TopContent pageTitle={labelConfig.bondContractListing} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 4, fontWeight: 600 }}>
              {isEdit ? "Edit Bond Contract" : "Create Bond Contract"}
            </h3>

            <p style={{ color: "#667085", fontSize: 14, marginBottom: 24 }}>
              {isEdit
                ? "Update the contract details below."
                : "Select a client first — other fields will populate automatically."}
            </p>

            <Form layout="vertical" form={form} onFinish={onFinish}>
              {/* ── Section 1: Contract Parties ───────────────────────── */}
              <SectionHeader title="Contract Parties" />

              {/* Client — live search select, same pattern as Treasury Bill */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Client"
                        tip="Type the full account number to search. Other fields populate after selection."
                      />
                    }
                    name="clientId"
                    rules={[{ required: true, message: "Client is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={clientOptions}
                      placeholder="Type account number to search…"
                      showSearch
                      filterOption={false}
                      onSearch={searchClients}
                      onSelect={handleClientSelect}
                      loading={clientSearching}
                      notFoundContent={
                        clientSearching ? (
                          <span style={{ color: "#667085", fontSize: 13 }}>
                            Searching…
                          </span>
                        ) : clientSearchValue.length >= 2 ? (
                          <div
                            style={{ padding: "8px 12px", textAlign: "center" }}
                          >
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                color: "#344054",
                                fontSize: 13,
                              }}
                            >
                              No account found
                            </p>
                            <p
                              style={{
                                margin: 0,
                                color: "#667085",
                                fontSize: 12,
                              }}
                            >
                              No client matched "{clientSearchValue}"
                            </p>
                          </div>
                        ) : (
                          <span style={{ color: "#667085", fontSize: 13 }}>
                            Type at least 2 characters
                          </span>
                        )
                      }
                      optionRender={(option) => (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            padding: "2px 0",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 13,
                              color: "#344054",
                            }}
                          >
                            {option.data.accountNo}
                          </span>
                          <span style={{ fontSize: 12, color: "#667085" }}>
                            {option.data.displayName}
                          </span>
                          {option.data.externalId && (
                            <span style={{ fontSize: 11, color: "#98A2B3" }}>
                              Ref: {option.data.externalId}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {/* All dependent fields — spin while template loads */}
              <Spin spinning={templateLoading}>
                <Row>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text=" Security Instrument"
                          tip="The bond Instrument for this contract."
                        />
                      }
                      name="productId"
                      rules={[
                        { required: true, message: "Product is required" },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={productOptions}
                        placeholder={
                          dependentFieldsLocked
                            ? "Select a client first"
                            : "Select Product"
                        }
                        showSearch
                        optionFilterProp="label"
                        disabled={dependentFieldsLocked}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Settlement Account"
                          tip="The client account used for settlement."
                        />
                      }
                      name="settlementAccountId"
                      rules={[
                        {
                          required: true,
                          message: "Settlement account is required",
                        },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={settlementAccountOptions}
                        placeholder={
                          dependentFieldsLocked
                            ? "Select a client first"
                            : "Select Settlement Account"
                        }
                        showSearch
                        optionFilterProp="label"
                        disabled={dependentFieldsLocked}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Custodian"
                          tip="The custodian holding the bond."
                        />
                      }
                      name="custodian"
                      rules={[
                        { required: true, message: "Custodian is required" },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={custodianOptions}
                        placeholder={
                          dependentFieldsLocked
                            ? "Select a client first"
                            : "Select Custodian"
                        }
                        showSearch
                        optionFilterProp="label"
                        disabled={dependentFieldsLocked}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Investor Type"
                          tip="The classification of the investor."
                        />
                      }
                      name="investerType"
                      initialValue={100}
                      rules={[
                        {
                          required: true,
                          message: "Investor type is required",
                        },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={investorTypeOptions}
                        placeholder="Select Investor Type"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── Section 2: Bond Details ───────────────────────────── */}
                <SectionHeader title="Bond Details" />
                <Row>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Bond Type"
                          tip="The type of bond being contracted."
                        />
                      }
                      name="bondType"
                      initialValue="FIXED_RATE_BOND"
                      rules={[
                        { required: true, message: "Bond type is required" },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={bondTypeOptions}
                        placeholder="Select Bond Type"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Issuer"
                          tip="The entity issuing the bond (e.g. Government, Corporate)."
                        />
                      }
                      name="issuer"
                      rules={[
                        { required: true, message: "Issuer is required" },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={[
                          { label: "Government", value: "Government" },
                          { label: "Corporate", value: "Corporate" },
                          { label: "Municipal", value: "Municipal" },
                          { label: "Agency", value: "Agency" },
                        ]}
                        placeholder="Select Issuer"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Fixed Coupon Rate (%)"
                          tip="The fixed interest rate paid on the bond."
                        />
                      }
                      name="fixedCouponRate"
                      rules={[
                        { required: true, message: "Coupon rate is required" },
                        { type: "number", min: 0 },
                      ]}
                    >
                      <InputNumber
                        style={inputStyle}
                        placeholder="e.g. 13.50"
                        precision={0}
                        min={0}
                        max={100}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Payment Type"
                          tip="How coupon payments are made."
                        />
                      }
                      name="paymentType"
                      initialValue={2}
                      rules={[
                        {
                          required: true,
                          message: "Payment type is required",
                        },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={paymentTypeOptions}
                        placeholder="Select Payment Type"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── Section 3: Dates & Tenor ──────────────────────────── */}
                <SectionHeader title="Dates & Tenor" />
                <Row>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Deal Initiation Date"
                          tip="The date the bond deal was initiated."
                        />
                      }
                      name="dealInitiationDate"
                      rules={[
                        {
                          required: true,
                          message: "Deal initiation date is required",
                        },
                      ]}
                    >
                      <DatePicker
                        style={inputStyle}
                        format="DD MMM YYYY"
                        placeholder="e.g. 29 APR 2026"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Issue Date"
                          tip="The date the bond is issued."
                        />
                      }
                      name="issueDate"
                      rules={[
                        { required: true, message: "Issue date is required" },
                      ]}
                    >
                      <DatePicker
                        style={inputStyle}
                        format="DD MMM YYYY"
                        placeholder="e.g. 01 MAY 2026"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Tenor (Days)"
                          tip="The number of days from issue to maturity."
                        />
                      }
                      name="tenor"
                      rules={[
                        { required: true, message: "Tenor is required" },
                        { type: "number", min: 1 },
                      ]}
                    >
                      <InputNumber
                        style={inputStyle}
                        placeholder="e.g. 365"
                        min={1}
                        precision={0}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── Section 4: Financial Details ─────────────────────── */}
                <SectionHeader title="Financial Details" />
                <Row>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Amount"
                          tip="The face value / par value of the bond."
                        />
                      }
                      name="amount"
                      rules={[
                        { required: true, message: "Amount is required" },
                        { type: "number", min: 1 },
                      ]}
                    >
                      <InputNumber
                        style={inputStyle}
                        placeholder="e.g. 1,000,000.00"
                        formatter={fmtAmount}
                        parser={parseAmount}
                        precision={2}
                        min={0}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Purchase Price"
                          tip="The actual price paid to purchase the bond."
                        />
                      }
                      name="purchasePrice"
                      rules={[
                        {
                          required: true,
                          message: "Purchase price is required",
                        },
                        { type: "number", min: 1 },
                      ]}
                    >
                      <InputNumber
                        style={inputStyle}
                        placeholder="e.g. 980,000.00"
                        formatter={fmtAmount}
                        parser={parseAmount}
                        precision={2}
                        min={0}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Number of Units"
                          tip="The number of bond units being purchased."
                        />
                      }
                      name="numberOfUnits"
                      rules={[
                        {
                          required: true,
                          message: "Number of units is required",
                        },
                        { type: "number", min: 1 },
                      ]}
                    >
                      <InputNumber
                        style={inputStyle}
                        placeholder="e.g. 1000"
                        min={1}
                        precision={0}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Total Consideration"
                          tip="Total amount payable (units × purchase price)."
                        />
                      }
                      name="totalConsideration"
                      rules={[
                        {
                          required: true,
                          message: "Total consideration is required",
                        },
                        { type: "number", min: 1 },
                      ]}
                    >
                      <InputNumber
                        style={inputStyle}
                        placeholder="e.g. 980,000.00"
                        formatter={fmtAmount}
                        parser={parseAmount}
                        precision={2}
                        min={0}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── Section 5: Compliance & Risk ─────────────────────── */}
                <SectionHeader title="Compliance & Risk" />
                <Row gutter={[16, 0]}>
                  {[
                    {
                      name: "kycStatus",
                      label: "KYC Status",
                      tip: "Know Your Customer check status.",
                      init: 0,
                      options: kycStatusOptions,
                    },
                    {
                      name: "amlScreening",
                      label: "AML Screening",
                      tip: "Anti-money laundering screening status.",
                      init: 100,
                      options: amlScreeningOptions,
                    },
                    {
                      name: "sanctionScreening",
                      label: "Sanction Screening",
                      tip: "Sanction list screening status.",
                      init: 0,
                      options: sanctionScreeningOptions,
                    },
                    {
                      name: "centralBankReport",
                      label: "Central Bank Report",
                      tip: "Central bank reporting status.",
                      init: 0,
                      options: centralBankReportOptions,
                    },
                    {
                      name: "issuerRiskRating",
                      label: "Issuer Risk Rating",
                      tip: "Risk rating of the bond issuer.",
                      init: 0,
                      options: issuerRiskRatingOptions,
                    },
                    {
                      name: "marketRiskConsideration",
                      label: "Market Risk Consideration",
                      tip: "Market risk assessment status.",
                      init: 0,
                      options: marketRiskConsiderationOptions,
                    },
                    {
                      name: "concentrationRisk",
                      label: "Concentration Risk",
                      tip: "Portfolio concentration risk status.",
                      init: 0,
                      options: SCORE_OPTIONS,
                    },
                  ].map(({ name, label, tip, init, options }) => (
                    <Col span={12} key={name} style={{ padding: "0 8px" }}>
                      <Form.Item
                        label={<Tip text={label} tip={tip} />}
                        name={name}
                        initialValue={init}
                        rules={[
                          { required: true, message: `${label} is required` },
                        ]}
                      >
                        <Select
                          style={{ height: 44 }}
                          suffixIcon={<ChevronDown color="#667085" />}
                          options={options}
                          placeholder={`Select ${label}`}
                        />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>

                {/* ── Section 6: Documentation Status ─────────────────── */}
                <SectionHeader title="Documentation Status" />
                <Row gutter={[16, 0]}>
                  {[
                    {
                      name: "applicationFormStatus",
                      label: "Application Form",
                      tip: "Status of the application form.",
                    },
                    {
                      name: "bondPurchaseAgreement",
                      label: "Bond Purchase Agreement",
                      tip: "Status of the bond purchase agreement.",
                    },
                    {
                      name: "identificationDocuments",
                      label: "Identification Documents",
                      tip: "Status of identification documents.",
                    },
                    {
                      name: "corporateResolution",
                      label: "Corporate Resolution",
                      tip: "Status of the corporate resolution.",
                    },
                    {
                      name: "taxClearanceCertificate",
                      label: "Tax Clearance Certificate",
                      tip: "Status of the tax clearance certificate.",
                    },
                  ].map(({ name, label, tip }) => (
                    <Col span={12} key={name} style={{ padding: "0 8px" }}>
                      <Form.Item
                        label={<Tip text={label} tip={tip} />}
                        name={name}
                        initialValue={100}
                        rules={[
                          { required: true, message: `${label} is required` },
                        ]}
                      >
                        <Select
                          style={{ height: 44 }}
                          suffixIcon={<ChevronDown color="#667085" />}
                          options={documentStatusOptions}
                          placeholder={`Select ${label}`}
                        />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>

                <div
                  style={{
                    borderTop: "1px solid #EAECF0",
                    margin: "8px 0 20px",
                  }}
                />

                <Row justify="end">
                  <Col>
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      style={{
                        height: 44,
                        padding: "0 24px",
                        borderRadius: 8,
                        border: "1px solid #D0D5DD",
                        background: "#fff",
                        color: "#344054",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: "pointer",
                        marginRight: 12,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => form.submit()}
                      style={{
                        height: 44,
                        padding: "0 24px",
                        borderRadius: 8,
                        border: "1px solid #BA24D5",
                        background: "#BA24D5",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      {isEdit ? "Update" : "Submit"}
                    </button>
                  </Col>
                </Row>
              </Spin>
              {/* ── end dependent fields Spin ── */}

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(AddBondContract);
